import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  FileText, 
  Download, 
  Calendar, 
  Filter, 
  RefreshCw, 
  FileSpreadsheet,
  File,
  TrendingUp,
  Droplet,
  IndianRupee,
  Wrench,
  Building,
  Loader2,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import api from '../services/api';

const ReportsModule = () => {
  const [selectedReport, setSelectedReport] = useState('summary');
  const [dateRange, setDateRange] = useState('last30days');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const reportTypes = [
    { id: 'summary', name: 'Executive Summary', desc: 'Overview of all key metrics', icon: FileText, color: 'cyan' },
    { id: 'coverage', name: 'Water Coverage', desc: 'Area-wise coverage analysis', icon: TrendingUp, color: 'green' },
    { id: 'nrw', name: 'NRW Analysis', desc: 'Non-Revenue Water report', icon: Droplet, color: 'blue' },
    { id: 'revenue', name: 'Revenue Report', desc: 'Billing & collection analysis', icon: IndianRupee, color: 'yellow' },
    { id: 'maintenance', name: 'Maintenance Report', desc: 'Asset maintenance summary', icon: Wrench, color: 'orange' },
    { id: 'assets', name: 'Asset Health', desc: 'Infrastructure status report', icon: Building, color: 'purple' }
  ];

  useEffect(() => {
    fetchReport();
  }, [selectedReport]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await api.client.get('/reports/comprehensive', {
        params: { report_type: selectedReport }
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await api.client.get(`/reports/export/${format}`, {
        params: { report_type: selectedReport },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SWATI_Report_${selectedReport}_${new Date().toISOString().slice(0,10)}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
    } finally {
      setExporting(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(2)} L`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString('en-IN') || '0';
  };

  const getColorClass = (color) => {
    const colors = {
      cyan: 'from-cyan-500/20 to-cyan-600/20 border-cyan-500/30',
      green: 'from-green-500/20 to-green-600/20 border-green-500/30',
      blue: 'from-blue-500/20 to-blue-600/20 border-blue-500/30',
      yellow: 'from-yellow-500/20 to-yellow-600/20 border-yellow-500/30',
      orange: 'from-orange-500/20 to-orange-600/20 border-orange-500/30',
      purple: 'from-purple-500/20 to-purple-600/20 border-purple-500/30'
    };
    return colors[color] || colors.cyan;
  };

  return (
    <div className="space-y-6" data-testid="reports-module">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">
            Comprehensive Reports
          </h1>
          <p className="text-gray-400 mt-1">Generate and export detailed analytics reports</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="border-cyan-500/30 text-cyan-400"
            onClick={() => fetchReport()}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          const isSelected = selectedReport === report.id;
          return (
            <button
              key={report.id}
              onClick={() => setSelectedReport(report.id)}
              className={`p-4 rounded-xl border-2 text-left transition-all bg-gradient-to-br ${getColorClass(report.color)} ${
                isSelected ? 'ring-2 ring-cyan-400 scale-105' : 'hover:scale-102'
              }`}
              data-testid={`report-type-${report.id}`}
            >
              <Icon className={`w-6 h-6 mb-2 ${isSelected ? 'text-cyan-400' : 'text-gray-400'}`} />
              <div className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-gray-300'}`}>{report.name}</div>
              <div className="text-xs text-gray-500 mt-1">{report.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Filters & Export */}
      <Card className="bg-[#12172e]/80 border-cyan-500/20">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400 text-sm">Period:</span>
              </div>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-40 bg-slate-800/50 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="last7days">Last 7 Days</SelectItem>
                  <SelectItem value="last30days">Last 30 Days</SelectItem>
                  <SelectItem value="thismonth">This Month</SelectItem>
                  <SelectItem value="lastmonth">Last Month</SelectItem>
                  <SelectItem value="thisyear">This Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => handleExport('excel')}
                disabled={exporting || loading}
                className="bg-green-600/20 text-green-400 border border-green-500/30 hover:bg-green-600/30"
                data-testid="export-excel-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                Export Excel
              </Button>
              <Button 
                onClick={() => handleExport('pdf')}
                disabled={exporting || loading}
                className="bg-red-600/20 text-red-400 border border-red-500/30 hover:bg-red-600/30"
                data-testid="export-pdf-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <File className="w-4 h-4 mr-2" />}
                Export PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      {loading ? (
        <Card className="bg-[#12172e]/80 border-cyan-500/20">
          <CardContent className="p-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </CardContent>
        </Card>
      ) : reportData && (
        <div className="space-y-6">
          {/* Summary Report */}
          {reportData.summary && (
            <Card className="bg-[#12172e]/80 border-cyan-500/20">
              <CardHeader>
                <CardTitle className="text-cyan-400 flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Executive Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm">Total Schemes</p>
                    <p className="text-2xl font-bold text-white">{reportData.summary.total_schemes}</p>
                    <p className="text-green-400 text-sm">{reportData.summary.active_schemes} active</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm">Coverage</p>
                    <p className="text-2xl font-bold text-cyan-400">{reportData.summary.coverage_percentage}%</p>
                    <p className="text-gray-500 text-sm">{formatNumber(reportData.summary.households_served)} households</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm">Asset Health</p>
                    <p className="text-2xl font-bold text-green-400">{reportData.summary.asset_health_percentage}%</p>
                    <p className="text-gray-500 text-sm">{reportData.summary.total_assets} assets</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm">Alerts</p>
                    <p className="text-2xl font-bold text-red-400">{reportData.summary.critical_alerts}</p>
                    <p className="text-orange-400 text-sm">{reportData.summary.major_alerts} major</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* NRW Report */}
          {reportData.nrw && (
            <Card className="bg-[#12172e]/80 border-cyan-500/20">
              <CardHeader>
                <CardTitle className="text-cyan-400 flex items-center gap-2">
                  <Droplet className="w-5 h-5" /> NRW Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Average NRW</p>
                    <p className="text-3xl font-bold text-yellow-400">{reportData.nrw.average_nrw}%</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Daily Loss</p>
                    <p className="text-3xl font-bold text-orange-400">{formatNumber(reportData.nrw.total_daily_loss_kl)} KL</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Monthly Loss</p>
                    <p className="text-3xl font-bold text-red-400">₹{formatNumber(reportData.nrw.total_monthly_loss_inr)}</p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-2 text-gray-400">Scheme</th>
                        <th className="text-right p-2 text-gray-400">NRW %</th>
                        <th className="text-right p-2 text-gray-400">Daily Supply</th>
                        <th className="text-right p-2 text-gray-400">Daily Loss</th>
                        <th className="text-right p-2 text-gray-400">Monthly Loss (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.nrw.data?.slice(0, 10).map((item, idx) => (
                        <tr key={idx} className="border-b border-slate-800">
                          <td className="p-2 text-white">{item.scheme_name}</td>
                          <td className="p-2 text-right">
                            <Badge className={item.nrw_percentage > 20 ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}>
                              {item.nrw_percentage}%
                            </Badge>
                          </td>
                          <td className="p-2 text-right text-gray-300">{item.daily_supply_kl?.toFixed(1)} KL</td>
                          <td className="p-2 text-right text-orange-400">{item.daily_loss_kl?.toFixed(1)} KL</td>
                          <td className="p-2 text-right text-gray-300">₹{formatNumber(item.estimated_monthly_loss_inr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revenue Report */}
          {reportData.revenue && (
            <Card className="bg-[#12172e]/80 border-cyan-500/20">
              <CardHeader>
                <CardTitle className="text-cyan-400 flex items-center gap-2">
                  <IndianRupee className="w-5 h-5" /> Revenue Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm">Total Billed</p>
                    <p className="text-2xl font-bold text-white">₹{formatNumber(reportData.revenue.total_billed)}</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm">Total Collected</p>
                    <p className="text-2xl font-bold text-green-400">₹{formatNumber(reportData.revenue.total_collected)}</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm">Collection Efficiency</p>
                    <p className="text-2xl font-bold text-cyan-400">{reportData.revenue.collection_efficiency}%</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm">Overdue Amount</p>
                    <p className="text-2xl font-bold text-red-400">₹{formatNumber(reportData.revenue.overdue_amount)}</p>
                    <p className="text-gray-500 text-sm">{reportData.revenue.overdue_bills} bills</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Maintenance Report */}
          {reportData.maintenance && (
            <Card className="bg-[#12172e]/80 border-cyan-500/20">
              <CardHeader>
                <CardTitle className="text-cyan-400 flex items-center gap-2">
                  <Wrench className="w-5 h-5" /> Maintenance Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Total</p>
                    <p className="text-2xl font-bold text-white">{reportData.maintenance.total_scheduled}</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Completed</p>
                    <p className="text-2xl font-bold text-green-400">{reportData.maintenance.completed}</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">In Progress</p>
                    <p className="text-2xl font-bold text-cyan-400">{reportData.maintenance.in_progress}</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Overdue</p>
                    <p className="text-2xl font-bold text-red-400">{reportData.maintenance.overdue}</p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Completion Rate</p>
                    <p className="text-2xl font-bold text-cyan-400">{reportData.maintenance.completion_rate}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Assets Report */}
          {reportData.assets && (
            <Card className="bg-[#12172e]/80 border-cyan-500/20">
              <CardHeader>
                <CardTitle className="text-cyan-400 flex items-center gap-2">
                  <Building className="w-5 h-5" /> Asset Health Report
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-gray-400 text-sm mb-3">By Type</h4>
                    <div className="space-y-2">
                      {Object.entries(reportData.assets.by_type || {}).map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                          <span className="text-white">{type}</span>
                          <Badge className="bg-cyan-500/20 text-cyan-400">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-gray-400 text-sm mb-3">By Status</h4>
                    <div className="space-y-2">
                      {Object.entries(reportData.assets.by_status || {}).map(([status, count]) => (
                        <div key={status} className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                          <span className="text-white">{status}</span>
                          <Badge className={status === 'InService' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}>
                            {count}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-slate-800/50 rounded-lg text-center">
                  <p className="text-gray-400 text-sm">Overall Health Score</p>
                  <p className="text-4xl font-bold text-green-400">{reportData.assets.health_score}%</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Coverage Report */}
          {reportData.coverage && (
            <Card className="bg-[#12172e]/80 border-cyan-500/20">
              <CardHeader>
                <CardTitle className="text-cyan-400 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" /> Coverage by Area
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-2 text-gray-400">Area</th>
                        <th className="text-right p-2 text-gray-400">Population</th>
                        <th className="text-right p-2 text-gray-400">Households Served</th>
                        <th className="text-right p-2 text-gray-400">Total Households</th>
                        <th className="text-right p-2 text-gray-400">Coverage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.coverage?.slice(0, 15).map((area, idx) => (
                        <tr key={idx} className="border-b border-slate-800">
                          <td className="p-2 text-white">{area.area_name}</td>
                          <td className="p-2 text-right text-gray-300">{formatNumber(area.population)}</td>
                          <td className="p-2 text-right text-green-400">{formatNumber(area.households_served)}</td>
                          <td className="p-2 text-right text-gray-300">{formatNumber(area.households_total)}</td>
                          <td className="p-2 text-right">
                            <Badge className={area.coverage_percentage >= 90 ? 'bg-green-500/20 text-green-400' : area.coverage_percentage >= 70 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}>
                              {area.coverage_percentage}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsModule;
